import { gql } from '@apollo/client'; // Needed for defining GraphQL queries


export const GET_BASIC_CUSTOMER_DATA_QUERY  = gql`
	query basicCustomerData{
		currentCustomer {
			birthdate
			address {
				city
				latitude
				postalCode
				road
			}
			customerNumber
			firstName
			id
			frozen {
				is
			}
			lastName
			name
			isCompany
			balance {
				remainingBalance
				remainingCredit
				totalCredit
			}
		}
	}
`;

/**
 * Query to fetch customer's rights data
 * Includes state and frozen status
 */
export const GET_CUSTOMER_RIGHTS_DATA_QUERY = gql`
	query customerRightsData {
		currentCustomer {
			customerNumber
			state {
				id
				name
			}
			frozen {
				is
			}
		}
	}
`;

/**
 * Query to fetch customer's cards with full details
 * Includes card type information and transaction details
 * Supports pagination parameters: after, before, first, last
 * Includes pageInfo for cursor-based pagination
 */
export const GET_CARDS_CUSTOMER_DATA_QUERY = gql`
	query cardsCustomerData($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			cards(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					# Minimal field set to stay under API field cost limit (HC0047, maxFieldCost=1100)
					# maskedPan and name removed - fetched separately when needed
					id
					number
					limit {
						disabled {
							at
							isDisabled
						}
					}
					type {
						id
						name
						shortName
					}
				}
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
			}
		}
	}
`;

/**
 * Query to fetch card display fields (name and maskedPan) with minimal field cost
 * Used to supplement GET_CARDS_CUSTOMER_DATA_QUERY which doesn't include these expensive fields
 * Uses cursor pagination so large card collections are loaded completely
 */
export const GET_CARD_DISPLAY_FIELDS_QUERY = gql`
	query cardDisplayFields($after: String, $first: Int) {
		currentCustomer {
			customerNumber
			cards(after: $after, first: $first) {
				nodes {
					id
					name
					maskedPan
					reference1
					reference2
				}
					pageInfo {
						endCursor
						hasNextPage
					}
			}
		}
	}
`;

/**
 * Query to fetch the latest transaction for a SINGLE card (by card id)
 * Used for the "senast använd" (last used) column in the card list view
 * Minimal field set: only the card id (to verify the match) and the single
 * most recent transaction (at + amount). Where-filtered on card id so only
 * 1 card and 1 transaction are returned — keeps the API field cost minimal.
 */
export const GET_CARD_LATEST_TRANSACTION_QUERY = gql`
	query cardLatestTransaction($after: String, $before: String, $first: Int, $last: Int, $id: Int!) {
		currentCustomer {
			customerNumber
			cards(after: $after, before: $before, first: $first, last: $last, where: { id: { eq: $id } }) {
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
				nodes {
					id
					transactions(after: null, before: null, first: null, last: 1) {
						nodes {
							at
							amount
						}
					}
				}
			}
		}
	}
`;

/**
 * Query to fetch customer's bank cards
 * Includes payment method information and card type
 * Supports pagination parameters: after, before, first, last
 */
export const GET_BANKCARDS_CUSTOMER_DATA_QUERY = gql`
	query bankCardsCustomerData($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			bankCards(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					created
					expireDate
					id
					issuer
					maskedPan
					name
					paymentMethod
					updated
					type {
						id
						name
					}
				}
			}
		}
	}
`;

/**
 * Query to fetch customer's transactions with pagination - LIGHT VERSION
 * Uses cursor-based pagination with pageInfo
 * Supports pagination parameters: before, last, where
 * NOTE: Minimal fields for dashboard statistics only
 * Only fetches essential fields: amount, date, type, cardNumber, quantity
 */
export const GET_TRANSACTIONS_CUSTOMER_DATA_QUERY = gql`
	query transactionsCustomerData($before: String, $last: Int, $where: TransactionFilterInput) {
		currentCustomer {
			customerNumber
			transactions(before: $before, last: $last, where: $where) {
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
				nodes {
					amount
					at
					cardNumber
					quantity
					transactionType
				}
			}
		}
	}
`;

/**
 * Query to fetch customer's transactions with pagination - FULL VERSION
 * Uses cursor-based pagination with pageInfo
 * Supports pagination parameters: after, before, first, last
 * Supports filtering with where clause: transactionType, at (date range), quantity
 * NOTE: Full fields for transactions table display
 * Fetches all transaction details including product info, discounts, prices, quantities, etc
 */
export const GET_TRANSACTIONS_FULL_CUSTOMER_DATA_QUERY = gql`
	query transactionsFullCustomerData(
		$before: String
		$last: Int
		$where: TransactionFilterInput
	) {
		currentCustomer {
			customerNumber
			transactions(before: $before, last: $last, where: $where) {
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
				nodes {
					id
					amount
					at
					cardNumber
					price
					productId
					quantity
					siteId
					transactionType
				}
			}
		}
	}
`;

/**
 * Query to fetch customer's agreement approvals
 * Fetches agreements the customer has approved (e.g., App terms, Kundklubb)
 * Used to check if customer has active app agreement or other service agreements
 */
/**
 * Query to fetch customer's agreement approvals
 * Supports pagination: after, before, first, last parameters
 * 
 * AGREEMENT IDs (agreement.id values):
 * ============================================================================
 * 7   = Reklam - Allow marketing communications
 * 8   = Tredjepartsreklam - Allow third-party marketing communications
 * 332 = Autogiro - Allow automatic payment (Autogiro)
 * 347 = Allmänna villkor - Informed of general terms for customer card
 * 350 = Kundklubb - Customer Club general terms
 * 354 = Användarvillkor Qstar App - Q-Star App Terms & Conditions
 * ============================================================================
 * 
 * Example usage:
 * const { data } = useGetCustomer<CustomerAgreementsResponse>(
 *   GET_CUSTOMER_AGREEMENTS_QUERY,
 *   { first: 15, after: null, before: null, last: null }
 * );
 * 
 * Check for specific agreement:
 * const hasAppAgreement = data?.currentCustomer?.agreementApprovals?.nodes?.some(
 *   approval => approval.agreement.id === 354 // App Terms & Conditions
 * ) ?? false;
 */
export const GET_CUSTOMER_AGREEMENTS_QUERY = gql`
	query getCustomerAgreements($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			agreementApprovals(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					id
					updatedAt
					agreement {
						createdAt
						id
						name
						shortDescription
						type {
							id
							name
						}
					}
				}
			}
		}
	}
`;

/**
 * Query to fetch customer's contact details
 * Includes phone numbers, emails, and address information
 * Used to display customer contact information in ProfileInfo component
 */
export const GET_CONTACT_CUSTOMER_DATA_QUERY = gql`
	query contactCustomerData {
		currentCustomer {
			phoneNumbers {
				description
				id
				number
				verified
				verifiedAt
				type {
					id
					name
				}
				unverifiedPhoneNumber {
					codeExpiresAt
					id
					number
				}
			}
			emails {
				address
				id
				verified
				verifiedAt
				type {
					id
					name
				}
				unverifiedEmail {
					address
					codeExpiresAt
					id
				}
			}
			address {
				city
				latitude
				longitude
				postalCode
				road
				country {
					alpha2Code
					alpha3Code
					name
				}
			}
			customerNumber
			name
			nationalId
		}
	}
`;

/**
 * Query to fetch customer's card invoices with pagination
 * Uses cursor-based pagination with pageInfo
 * Supports pagination parameters: after, before, first, last
 * Fetches minimal invoice details: amount, dates, OCR
 * Uses last/before for backward pagination (most recent invoices first)
 */
export const GET_CARD_INVOICES_QUERY = gql`
	query cardInvoicesCustomerData($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			cardInvoices(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					amount
					dueDate
					id
					invoiceDate
					ocr
				}
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
			}
		}
	}
`;

/**
 * Query to fetch paid and remaining amounts for ALL card invoices (auto-pagination)
 * Fetches paid amount and remaining amount for mapping to invoices
 * Automatically paginates through all invoices to build complete map
 */
export const GET_CARD_INVOICES_PAID_REMAINS_QUERY = gql`
	query cardInvoicesPaidRemains($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			cardInvoices(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					id
					paid
					remains
				}
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
			}
		}
	}
`;

/**
 * Query to fetch delivery type information for ALL card invoices (auto-pagination)
 * Fetches delivery method details for mapping to invoices
 * Automatically paginates through all invoices to build complete map
 */
export const GET_CARD_INVOICES_DELIVERY_TYPE_QUERY = gql`
	query cardInvoicesDeliveryType($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			cardInvoices(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					id
					deliveryType {
						id
						name
					}
				}
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
			}
		}
	}
`;

/**
 * Query to fetch customer's prepaid statements with pagination
 * Uses cursor-based pagination with pageInfo
 * Supports pagination parameters: after, before, first, last
 * Fetches statement fields: id, statementDate, purchase, deliveryType
 * Uses last/before for backward pagination (most recent statements first)
 */
/**
 * Query to fetch the customer's preferred delivery type for invoices and statements
 * invoiceDelivery: customer's preferred delivery method for card invoices
 * statementDelivery: customer's preferred delivery method for prepaid statements (may be null if not set)
 *
 * deliveryType.id values:
 *   1 = Pappersutskick         - Paper mail
 *   2 = E-faktura              - E-invoice (personal)
 *   3 = E-faktura företag      - E-invoice (company)
 *   4 = E-postutskick          - Email
 *   5 = E-faktura företag + e-postutskick - E-invoice (company) + email
 */
export const GET_CUSTOMER_DELIVERY_TYPES_QUERY = gql`
	query customerDeliveryTypes {
		currentCustomer {
			customerNumber
			invoiceDelivery {
				deliveryType {
					id
					name
				}
			}
			statementDelivery {
				deliveryType {
					id
					name
				}
			}
		}
	}
`;

export const GET_CARD_STATEMENTS_QUERY = gql`
	query cardStatementsCustomerData($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			prepaidStatements(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					id
					statementDate
					purchase
					deliveryType {
						id
						name
					}
				}
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
			}
		}
	}
`;

/**
 * Query to fetch customer tasks.
 * Supports pagination via after/before/first/last.
 * Use network-only since tasks change after mutations.
 */
export const GET_CUSTOMER_TASKS_QUERY = gql`
	query getCustomerTasks($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			tasks(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					closedAt
					createdAt
					customerId
					description
					dueDate
					id
					type {
						id
						name
					}
				}
			}
		}
	}
`;

/**
 * Query to fetch user profiles under currentCustomer with pagination.
 * Fetches 10 at a time, follow hasNextPage to load all profiles.
 * Use network-only since user profiles change after mutations.
 */
export const GET_USER_PROFILES_QUERY = gql`
	query getUserProfiles($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			userProfiles(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					id
					email
					firstName
					lastName
					phoneNumber
					createdAt
					updatedAt
					identifier
				}
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
			}
		}
	}
`;

/**
 * Fetches user profile roles (id, roles, updatedAt) for all profiles under currentCustomer.
 * Split from GET_USER_PROFILES_QUERY to stay within API field cost limits.
 * Use with useGetAllUserProfileRoles hook - paginate until hasNextPage is false.
 */
export const GET_USER_PROFILE_ROLES_QUERY = gql`
	query getUserProfileRoles($after: String, $before: String, $first: Int, $last: Int) {
		currentCustomer {
			customerNumber
			userProfiles(after: $after, before: $before, first: $first, last: $last) {
				nodes {
					id
					roles {
						claimName
					}
					updatedAt
				}
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
			}
		}
	}
`;
export const GET_AUTOGIRO_DATA_QUERY = gql`
    query autogiroData {
        currentCustomer {
            autogiro {
                accountNumber
                approved
                changeRequested
                changeRequestedAt
                clearingNumber
            }
            customerNumber
        }
    }
`;
